import { AxiosError } from "axios";
import moment from "moment";
import { useState, useLayoutEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { AppBox, AppGridBox, AppBorderBox, AppLink, AppText } from "../../../../../../commons/components";
import { IServerErrorResponse } from "../../../../../../commons/components/interface";
import { StatusEnum } from "../../../../../../commons/enums/status-enum";
import { IFootballTeam } from "../../../../../../commons/models/football/interface/i-football-team";
import { getErrorKeyPosition } from "../../../../../../commons/utilities/form-utils";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks/app";
import { setStepperFormData, cleanUpStepperFormData } from "../../../../../../redux/slices/stepper-form-slice";
import { useSnackbar } from "../../../../../../redux/snackbarProvider";
import SubTeamSpecificsAddEdit from "./SubTeamSpecificsAddEdit";
import { formatDate } from "../../../../../../commons/utilities/date-utils";
import { subTeamAdd } from "../../../../../../redux/slices/football/admin/team/sub-team/manage-sub-team-add-slice";
import { subTeamEdit } from "../../../../../../redux/slices/football/admin/team/sub-team/manage-sub-team-edit-slice";
import { IFootballSubTeamDetail } from "../../../../../../commons/models/football/interface/i-football-sub-team";
import SubTeamBasicInfoAddEdit from "./SubTeamBasicInfoAddEdit";
import { ISubTeamDTO } from "../../../../../../api-services/football/admin/manage-football-team-service";
import FormImageAddEdit from "../../../../FormImageAddEdit";
import isNullOrUndefined from "../../../../../../commons/utilities/null-or-undefined";

interface SubTeamAddEditProps {
  teamInfo: IFootballTeam;
  subTeamData?: IFootballSubTeamDetail | null;
  onSave?: () => void;
}

const stepperFormOptions = [
  {
    step: 1,
    title: "Sub-team's Logo",
  },
  {
    step: 2,
    title: "Basic Information",
  },
  {
    step: 3,
    title: "Specifics",
  },
];

const stepperFromKeyMap = [
  ["logo"],
  ["name", "shortName", "abbreviation", "founded", "description"],
  ["homeColor", "awayColor", "thirdColor", "goalkeeperHomeColor", "goalkeeperAwayColor", "status"],
];

export default function SubTeamAddEdit({ teamInfo, subTeamData, onSave }: SubTeamAddEditProps) {
  const methods = useForm();
  const formErrors = methods.formState.errors;
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const [selectedStepperFormOption, setSelectedStepperFormOption] = useState(stepperFormOptions[0]);
  const formData = useAppSelector<ISubTeamDTO>((state) => state.stepperForm.stepperFormData);
  const { teamAddResponseStatus } = useAppSelector((state) => state.footballManageTeamAdd);
  const { teamEditResponseStatus } = useAppSelector((state) => state.footballManageTeamEdit);
  const { uploadedImageStatus } = useAppSelector((state) => state.imageUpload);

  //initiate the stepperData
  useLayoutEffect(() => {
    if (!subTeamData) {
      const subTeamData: ISubTeamDTO = {
        name: "",
        shortName: "",
        abbreviation: "",
        description: null,
        founded: null,
        logo: null,
        homeColor: null,
        awayColor: null,
        thirdColor: null,
        goalkeeperHomeColor: null,
        goalkeeperAwayColor: null,
        status: 1,
      };
      dispatch(setStepperFormData<ISubTeamDTO>(subTeamData));
    }
  }, []);

  useLayoutEffect(() => {
    if (subTeamData) {
      const teamDTO: ISubTeamDTO = {
        name: subTeamData.name,
        shortName: subTeamData.shortName,
        abbreviation: subTeamData.abbreviation,
        description: subTeamData.description,
        founded: subTeamData.founded,
        logo: subTeamData.logo,
        homeColor: subTeamData.homeColor,
        awayColor: subTeamData.awayColor,
        thirdColor: subTeamData.thirdColor,
        goalkeeperHomeColor: subTeamData.goalkeeperHomeColor,
        goalkeeperAwayColor: subTeamData.goalkeeperAwayColor,
        status: subTeamData.status,
      };
      dispatch(setStepperFormData<ISubTeamDTO>(teamDTO));
    }
  }, [subTeamData]);

  const handleNext = (currentStep: number) => {
    if (
      teamAddResponseStatus !== StatusEnum.Loading &&
      teamEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep]);
    }
  };
  const handleBack = (currentStep: number) => {
    if (
      teamAddResponseStatus !== StatusEnum.Loading &&
      teamEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep - 2]);
    }
  };

  const handleStepperOptionClick = (option: any) => {
    if (
      teamAddResponseStatus !== StatusEnum.Loading &&
      teamEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(option);
    }
  };

  const handleFormSubmit = () => {
    const formattedValues: any = {
      ...formData,
      logo: formData.logo ? formData.logo : null,
      status: Number(formData.status),
      founded: formData.founded ? formatDate(moment(formData.founded), "YYYY-MM-DD") : null,
    };
    dispatch(
      subTeamData
        ? subTeamEdit({ teamId: teamInfo.id, subTeamId: subTeamData.id, subTeamData: formattedValues })
        : subTeamAdd({ teamId: teamInfo.id, subTeamData: formattedValues })
    )
      .unwrap()
      .then(() => {
        if (subTeamData) {
          addSnackbar({
            key: "sub-team-edit-success",
            text: "Team Edited Successfully",
            variant: "success",
          });
        } else {
          addSnackbar({
            key: "sub-team-add-success",
            text: "Team Added Successfully",
            variant: "success",
          });
        }
        dispatch(cleanUpStepperFormData());
        if (onSave) {
          onSave();
        }
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        const responseData = error.response?.data;
        if (error.response?.status === 417) {
          addSnackbar({
            key: "error",
            text: "Form not valid",
            variant: "danger",
          });
          if (responseData) {
            const stepperPositionSet = new Set<number>();
            Object.entries(responseData).forEach(([field, messages]) => {
              messages.forEach((message: string) => {
                methods.setError(field, { message });
                const stepperPosition = getErrorKeyPosition(field, stepperFromKeyMap);
                if (stepperPosition) {
                  stepperPositionSet.add(stepperPosition.step);
                }
                const lowestStep = Math.min(...Array.from(stepperPositionSet));
                setSelectedStepperFormOption(stepperFormOptions[lowestStep]);
              });
            });
          }
        } else {
          addSnackbar({
            key: "error",
            text: responseData?.message,
            variant: "danger",
          });
        }
      });
  };

  const clearErrorField = (key: string) => {
    methods.clearErrors(key);
  };

  return (
    <FormProvider {...methods}>
      <AppBox flexDirection="column" gap="sm" style={{ height: "var(--modal-md)" }}>
        <AppGridBox className=" flex-1" style={{ gridTemplateColumns: "1fr 3fr" }} gap="sm">
          <AppBorderBox border={["Right"]}>
            <AppBox flexDirection="column" gap="2xs" className="w-100">
              {stepperFormOptions.map((option, index) => (
                <AppLink
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    handleStepperOptionClick(option);
                  }}
                >
                  <AppBorderBox
                    border={option.step === selectedStepperFormOption.step ? ["Right"] : []}
                    borderColor="primary"
                  >
                    <AppBox pl="sm" py="2xs">
                      <AppText
                        fontWeight="semibold"
                        size="lg"
                        color={option.step === selectedStepperFormOption.step ? "primary" : "currentColor"}
                      >
                        {option.title}
                      </AppText>
                    </AppBox>
                  </AppBorderBox>
                </AppLink>
              ))}
            </AppBox>
          </AppBorderBox>
          <AppBox flexDirection="column" className="w-100 flex-1">
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 1 ? { display: "flex" } : { display: "none" }}
            >
              <FormImageAddEdit
                image={subTeamData?.logo}
                DTOKey="logo"
                title="Sub-team's Logo"
                isEdit={!isNullOrUndefined(subTeamData)}
                formStep={selectedStepperFormOption.step as any}
                formErrors={formErrors}
                onSkipOrNext={handleNext}
                onClearError={clearErrorField}
                submitStatus={teamEditResponseStatus}
                onSaveAndExit={handleFormSubmit}
              />
            </AppBox>
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 2 ? { display: "flex" } : { display: "none" }}
            >
              <SubTeamBasicInfoAddEdit
                subTeamData={subTeamData}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                onBack={handleBack}
                onNext={handleNext}
                onClearError={clearErrorField}
                submitStatus={teamEditResponseStatus}
                onSaveAndExit={handleFormSubmit}
              />
            </AppBox>
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 3 ? { display: "flex" } : { display: "none" }}
            >
              <SubTeamSpecificsAddEdit
                subTeamData={subTeamData}
                formStep={selectedStepperFormOption.step}
                submitStatus={teamAddResponseStatus || teamEditResponseStatus}
                formErrors={formErrors}
                onBack={handleBack}
                onSubmit={handleFormSubmit}
                onClearError={clearErrorField}
              />
            </AppBox>
          </AppBox>
        </AppGridBox>
      </AppBox>
    </FormProvider>
  );
}
